/**
 * O sino de notificações.
 *
 * ## Por que ele consulta de tempos em tempos
 *
 * A liberação de um acesso acontece do lado do servidor, minutos depois do
 * pedido — e a pessoa está com a aba aberta esperando. Sem consulta periódica,
 * o aviso só apareceria se ela recarregasse a página, que é justamente o que
 * ela não sabe que precisa fazer.
 *
 * Trinta segundos é a folga entre "parece instantâneo" e "não vale uma conexão
 * permanente". WebSocket entregaria melhor e traria estado de conexão,
 * reconexão e um caminho a mais atravessando o Traefik — caro demais para
 * dezenas de avisos por dia.
 *
 * **A consulta para quando a aba está escondida.** Sem isso, uma aba esquecida
 * a semana inteira faria vinte mil requisições sem ninguém olhando.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ehConteudoPublicado } from '../rotas.js';
import { api, SessionError } from '../api.js';
import { emitAccessChanged, onPendingWatch, ACCESS_KINDS } from '../accessEvents.js';
import { relativeTime, exactTime } from '../time.js';
import { Icon } from './Icon.jsx';

const POLL_MS = 30_000;

/** Durante a espera por uma aprovação. Ver `emitPendingWatch`. */
const FAST_POLL_MS = 5_000;

/** Por quanto tempo o modo rápido dura antes de voltar ao normal. */
const FAST_WINDOW_MS = 3 * 60_000;

/**
 * Ícone e cor por tipo de aviso.
 *
 * A cor carrega metade da informação: verde e vermelho separam aprovado de
 * recusado antes de a pessoa ler a frase. Os neutros ficam em cinza para não
 * disputar atenção com os dois que exigem reação.
 */
const ICONS = {
  WELCOME: { name: 'sparkles', tone: 'gold' },
  REQUEST_SENT: { name: 'send', tone: 'muted' },
  REQUEST_APPROVED: { name: 'check-circle', tone: 'ok' },
  REQUEST_REJECTED: { name: 'x-circle', tone: 'danger' },
  ACCESS_REVOKED: { name: 'lock', tone: 'muted' },
  REQUEST_RECEIVED: { name: 'inbox', tone: 'info' },
  ITEM_PUBLISHED: { name: 'package', tone: 'gold' },
  // O canal de mensagens. Enquanto a resposta a uma dúvida saía como
  // `REQUEST_RECEIVED`, ela vinha com o ícone de caixa de entrada de PEDIDO DE
  // ACESSO — e, pior, levava para a tela de solicitações.
  FEEDBACK_RECEIVED: { name: 'inbox', tone: 'info' },
  FEEDBACK_ANSWERED: { name: 'send', tone: 'ok' },
};

export function Bell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const boxRef = useRef(null);
  const navigate = useNavigate();

  // Consulta acelerada enquanto há pedido em andamento.
  const [fast, setFast] = useState(false);
  const fastTimer = useRef(null);

  // Ids já vistos. Serve para reconhecer o que chegou AGORA — sem isso, toda
  // consulta reavisaria o portfólio, e ele recarregaria a cada 30 segundos.
  const seen = useRef(null);

  const load = useCallback(async () => {
    try {
      const fresh = await api.notifications();
      setData(fresh);

      const ids = new Set(fresh.notifications.map((n) => n.id));

      // Na primeira consulta só se anota o que existe: o que já estava lá
      // quando a tela abriu não é novidade, e recarregar por causa disso
      // faria o portfólio piscar sozinho ao entrar.
      if (seen.current === null) {
        seen.current = ids;
        return;
      }

      const novidade = fresh.notifications.some(
        (n) => !seen.current.has(n.id) && ACCESS_KINDS.includes(n.kind),
      );
      seen.current = ids;

      // O portfólio está aberto atrás deste sino, dizendo "Sem acesso" para
      // algo que acabou de ser liberado. Ele precisa saber.
      if (novidade) {
        emitAccessChanged();
        // Chegou o que se esperava: volta ao ritmo normal em vez de deixar a
        // janela de três minutos correr à toa.
        clearTimeout(fastTimer.current);
        setFast(false);
      }
    } catch (err) {
      // Sessão expirada não vira alarme aqui: o sino é acessório, e quem
      // manda a pessoa para o login é a tela que ela está usando.
      if (!(err instanceof SessionError)) return;
    }
  }, []);

  // Alguém acabou de pedir acesso: acelera por alguns minutos e volta ao
  // normal. O `clearTimeout` reinicia a janela a cada novo pedido, em vez de
  // deixar o primeiro decidir quando ela acaba.
  useEffect(
    () =>
      onPendingWatch(() => {
        setFast(true);
        clearTimeout(fastTimer.current);
        fastTimer.current = setTimeout(() => setFast(false), FAST_WINDOW_MS);
      }),
    [],
  );

  useEffect(() => () => clearTimeout(fastTimer.current), []);

  useEffect(() => {
    load();
    const timer = setInterval(
      () => {
        // `hidden` cobre aba em segundo plano e janela minimizada. Uma aba
        // esquecida não deve gastar requisição.
        if (!document.hidden) load();
      },
      fast ? FAST_POLL_MS : POLL_MS,
    );

    // Voltar para a aba busca na hora, em vez de esperar o próximo ciclo:
    // é o momento em que a pessoa mais espera ver novidade.
    const onVisible = () => {
      if (!document.hidden) load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, fast]);

  // Fecha ao clicar fora e ao teclar Esc, como o menu do usuário.
  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  async function markAll() {
    try {
      setData(await api.markNotificationsRead());
    } catch {
      // Falhar em marcar como lida não merece alarme: a próxima consulta
      // corrige, e insistir numa mensagem de erro por isso irrita mais.
    }
  }

  async function openNotification(n) {
    setOpen(false);
    if (!n.readAt) {
      try {
        setData(await api.markNotificationsRead(n.id));
      } catch {
        /* idem */
      }
    }
    if (!n.link) return;

    // Conteúdo publicado não passa pelo roteador: ele é servido pelo Express,
    // fora do pacote do Vite. `navigate()` trocaria a URL, não acharia rota
    // nenhuma, e a aplicação desenharia "esta página não existe" sobre um
    // endereço que abre normalmente. Ver `rotas.js` — foi um defeito real.
    if (ehConteudoPublicado(n.link)) {
      window.location.assign(n.link);
      return;
    }

    navigate(n.link);
  }

  const { notifications, unread } = data;

  return (
    <div className="bell-area" ref={boxRef}>
      <button
        type="button"
        className="bell-button"
        data-tour="sino"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={unread > 0 ? `Notificações: ${unread} não lidas` : 'Notificações'}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="bell-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 8.5a6 6 0 1 0-12 0c0 5.2-2 6.7-2 6.7h16s-2-1.5-2-6.7Z" />
            <path d="M13.7 19a2 2 0 0 1-3.4 0" />
          </svg>
        </span>
        {unread > 0 && (
          <span className="bell-count" aria-hidden="true">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="bell-panel" role="menu">
          <div className="bell-head">
            <strong>Notificações</strong>
            {unread > 0 && (
              <button type="button" className="bell-mark" onClick={markAll}>
                Marcar todas como lidas
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <p className="bell-empty">Nada por aqui ainda.</p>
          ) : (
            <ul className="bell-list">
              {notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    role="menuitem"
                    className={`bell-item ${n.readAt ? '' : 'is-unread'}`}
                    onClick={() => openNotification(n)}
                  >
                    <span className="bell-item-icon">
                      <Icon
                        name={ICONS[n.kind]?.name ?? 'sparkles'}
                        tone={ICONS[n.kind]?.tone ?? 'muted'}
                      />
                    </span>
                    <span className="bell-item-text">
                      <span className="bell-item-title">{n.title}</span>
                      {n.body && <span className="bell-item-body">{n.body}</span>}
                      <time
                        className="bell-item-when"
                        dateTime={n.createdAt}
                        title={exactTime(n.createdAt)}
                      >
                        {relativeTime(n.createdAt)}
                      </time>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default Bell;
